// Exchange detail — the single canvas for managing one exchange end-to-end.
//
// One page: a sticky action bar with the state machine's primary CTAs, the
// embedded conversation (ThreadView), the address card once confirmed, the
// collapsible offer + terms below, and the share / recap / certificate
// affordances grouped in their own row. /inbox/$threadId stays available as
// the triage entry point but uses the same ExchangeActions component so
// users never see one action on one page and not the other.
//
// Data fetch is now react-query keyed ['exchange', id] so the thread's
// ThreadDO state_changed fanout (which invalidates ['exchange']) refreshes
// the bar + banners live when the counterparty acts.

import {
  Alert,
  Anchor,
  Avatar,
  Box,
  Button,
  Card,
  Collapse,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { SLEEPING_AMENITIES } from '@wwaave/shared/vocab';
import { useEffect, useState } from 'react';

import { AppShell } from '../../components/app-shell/AppShell';
import { ExchangeActions } from '../../components/ExchangeActions';
import { SmartRiderPanel } from '../../components/exchanges/SmartRiderPanel';
import { ReportButton } from '../../components/ReportButton';
import { StateBadge } from '../../components/StateBadge';
import { EmptyState, MotionSection, SectionTitle } from '../../components/ui';
import { fetchOk } from '../../lib/api';
import { track } from '../../lib/telemetry';
import { showToast } from '../../lib/toast';
import { ThreadView } from '../inbox/$threadId';

export const Route = createFileRoute('/exchanges/$id')({
  component: ExchangeDetail,
});

type DetailState =
  | 'drafted'
  | 'submitted'
  | 'confirmed'
  | 'completed'
  | 'cancelled'
  | 'declined'
  | 'disputed'
  | 'closed';

type ExchangeDetailDto = {
  exchange: {
    id: string;
    state: DetailState;
    startDate: string;
    endDate: string;
    partySize: number;
    artFormSlug: string;
    openingLetter: string;
    stipendOffered: boolean;
    stipendAmountMinor: number | null;
    stipendCurrency: string | null;
    threadId: string | null;
    submittedAt: string | null;
    decidedAt: string | null;
    cancelledAt: string | null;
    cancellationReason: string | null;
    cancellationInitiator: 'artist' | 'host' | 'system' | null;
    completedAt: string | null;
    createdAt: string;
    updatedAt: string;
    accommodationKind?: string | null;
    accommodationNotes?: string | null;
    mealsBreakfast?: boolean;
    mealsLunch?: boolean;
    mealsDinner?: boolean;
    mealsNotes?: string | null;
    travelContributionOffered?: boolean;
    travelContributionAmountMinor?: number | null;
    travelContributionCurrency?: string | null;
    equipmentOffered?: boolean;
    equipmentNotes?: string | null;
    promotionOffered?: boolean;
    promotionNotes?: string | null;
    recordingOffered?: boolean;
    recordingNotes?: string | null;
  };
  venue: {
    id: string;
    name: string;
    slug: string;
    city: string;
    country: string;
    addressLine: string | null;
  };
  artist: { id: string; displayName: string; slug: string | null; profilePhotoUrl: string | null };
  host: { id: string; displayName: string };
  viewer: { role: 'artist' | 'host' | 'admin'; addressVisible: boolean };
  smartRiderEnabled: boolean;
};

// Shape returned by GET /api/shares/:exchangeId. The endpoint 404s unless the
// exchange is `completed` AND both reviews are `released`, so we only render
// the share card on a successful fetch.
type ShareDto = {
  exchangeId: string;
  artist: { displayName: string; slug: string | null };
  venue: { name: string; city: string; slug: string };
  monthLabel: string;
  canonicalUrl: string;
  imageUrl: string;
  shareText: string;
  twitterIntent: string;
};

// Subset of GET /api/recaps/me/exchange/:id we use here — just enough to pick
// the right "Draft a recap" CTA. Full shape lives in $id.recap.tsx.
type RecapMeDto = {
  recap: {
    id: string;
    state: 'drafted' | 'pending_approval' | 'published' | 'declined';
    slug: string | null;
  } | null;
  eligible: boolean;
};

function ExchangeDetail() {
  const { id } = Route.useParams();

  const detailQuery = useQuery<ExchangeDetailDto>({
    queryKey: ['exchange', id],
    queryFn: async () => {
      const res = await fetchOk(`/api/exchanges/${encodeURIComponent(id)}`, {
        credentials: 'include',
      });
      return (await res.json()) as ExchangeDetailDto;
    },
  });
  const data = detailQuery.data;

  // Share-card data. Endpoint 404s until the exchange is completed AND both
  // reviews have released — `enabled` keeps us off the wire entirely until the
  // exchange detail confirms `state === 'completed'`.
  const isCompleted = data?.exchange.state === 'completed';
  const isRecapEligible =
    data?.exchange.state === 'completed' || data?.exchange.state === 'closed';
  const shareQuery = useQuery<ShareDto>({
    queryKey: ['share', id],
    enabled: isCompleted,
    retry: false,
    queryFn: async () => {
      const res = await fetchOk(`/api/shares/${encodeURIComponent(id)}`, {
        credentials: 'include',
      });
      return (await res.json()) as ShareDto;
    },
  });
  const share = shareQuery.data;

  const recapQuery = useQuery<RecapMeDto>({
    queryKey: ['recap', id],
    enabled: isRecapEligible,
    retry: false,
    queryFn: async () => {
      const res = await fetchOk(`/api/recaps/me/exchange/${encodeURIComponent(id)}`, {
        credentials: 'include',
      });
      return (await res.json()) as RecapMeDto;
    },
  });
  const recapMe = recapQuery.data;

  if (detailQuery.isError) {
    const message =
      detailQuery.error instanceof Error
        ? detailQuery.error.message
        : "Couldn't load this exchange.";
    return (
      <AppShell tone="content">
        <Alert color="red">{message}</Alert>
      </AppShell>
    );
  }
  if (data === undefined) {
    return (
      <AppShell tone="content">
        <Group justify="center">
          <Loader />
        </Group>
      </AppShell>
    );
  }

  const { exchange, venue, artist, host, viewer } = data;
  const counterpartyName = viewer.role === 'host' ? artist.displayName : host.displayName;

  const artistLink =
    viewer.role === 'host' && artist.slug !== null ? (
      <Link to="/artists/$slug" params={{ slug: artist.slug }} style={{ textDecoration: 'none' }}>
        <Anchor component="span" fw={700}>
          {artist.displayName}
        </Anchor>
      </Link>
    ) : null;

  const headerTitle =
    viewer.role === 'host' ? (
      <>Request from {artistLink ?? artist.displayName}</>
    ) : (
      `Stay at ${venue.name}`
    );

  // Day-of-arrival surface — see helper below.
  const dayOfArrival = computeDayOfArrival(
    exchange.state,
    exchange.startDate,
    exchange.endDate,
  );

  // Hide the "post-state" links (review / certificate / recap / dispute) when
  // there's nothing to show. Keeps the row from rendering an empty Group.
  const hasPostStateLinks =
    exchange.state === 'completed' ||
    exchange.state === 'closed' ||
    (exchange.state === 'confirmed' && (viewer.role === 'artist' || viewer.role === 'host'));

  return (
    <AppShell tone="content">
        <Stack gap="lg">
          {/* Sticky action bar — counterparty + dates + state + primary CTAs.
              Stays in view as the conversation scrolls so the next action is
              never more than a glance away. `top: 64px` clears AppHeader
              (sticky, minHeight 64 from HeaderShell). z-index < AppHeader's 50
              so the header always wins when both want the top of the screen. */}
          <Box
            style={{
              position: 'sticky',
              top: 64,
              zIndex: 5,
              background: 'var(--mantine-color-body)',
              paddingTop: 12,
              paddingBottom: 12,
              borderBottom: '1px solid var(--mantine-color-ink-1)',
            }}
          >
            <Stack gap="xs">
              {/* §6 — back affordance; exchanges are reached from the inbox. */}
              <Link to="/inbox" style={{ textDecoration: 'none', width: 'fit-content' }}>
                <Anchor component="span" size="sm" c="ink.6">
                  ← Inbox
                </Anchor>
              </Link>
              <Group justify="space-between" wrap="wrap" gap="sm">
                <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                  <Text
                    size="xs"
                    c="brand.7"
                    fw={600}
                    style={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}
                  >
                    Exchange
                  </Text>
                  <Title order={3} style={{ letterSpacing: '-0.01em', lineHeight: 1.2 }}>
                    {headerTitle}
                  </Title>
                  <Text size="sm" c="ink.6">
                    {exchange.startDate} → {exchange.endDate} · party of {exchange.partySize}
                  </Text>
                </Stack>
                {/* §3 — report-a-problem on the booking. Sits beside the state
                    badge so it's discoverable to both parties (same header slot
                    as the profile pages) without crowding the title. The icon
                    variant matches the inbox thread, the exchange's sibling
                    surface. */}
                <Group gap="xs" wrap="nowrap" align="center" style={{ flexShrink: 0 }}>
                  <StateBadge entity="exchange" state={exchange.state} size="lg" />
                  <ReportButton
                    subjectType="exchange"
                    subjectId={exchange.id}
                    subjectLabel={counterpartyName}
                    variant="icon"
                  />
                </Group>
              </Group>
              <ExchangeActions
                exchange={{ id: exchange.id, state: exchange.state }}
                viewer={{ role: viewer.role }}
                artistDisplayName={artist.displayName}
                counterpartyName={counterpartyName}
                onAfterAction={async () => {
                  await detailQuery.refetch();
                }}
              />
            </Stack>
          </Box>

          {dayOfArrival !== null ? (
            <MotionSection>
              <DayOfArrivalCard
                phase={dayOfArrival}
                venueName={venue.name}
                address={viewer.addressVisible ? venue.addressLine : null}
              />
            </MotionSection>
          ) : null}

          {/* Address surface. Once the exchange is confirmed and the address
              is visible to the viewer, we render the full address inline
              instead of pointing the user "into the thread". */}
          <MotionSection delay={0.02}>
            <AddressCard
              state={exchange.state}
              venue={venue}
              addressVisible={viewer.addressVisible}
            />
          </MotionSection>

          {/* §6.4 — once a stay is locked in (confirmed) and the address has
              been revealed, nudge the travelling artist to let a trusted
              contact know where they'll be. Calm, supportive, never alarming;
              no backend — the share text is assembled from the venue + dates
              already on the page. */}
          {exchange.state === 'confirmed' && viewer.role === 'artist' ? (
            <MotionSection delay={0.03}>
              <ShareYourStayCard
                venue={venue}
                startDate={exchange.startDate}
                endDate={exchange.endDate}
                addressLine={viewer.addressVisible ? venue.addressLine : null}
              />
            </MotionSection>
          ) : null}

          {/* §3 — light identity match. A one-line, no-tech reassurance for the
              host: when the artist arrives, a quick glance against the profile
              photo. No stored selfie, nothing to upload. */}
          {exchange.state === 'confirmed' && viewer.role === 'host' ? (
            <MotionSection delay={0.03}>
              <IdentityMatchNudge artist={artist} />
            </MotionSection>
          ) : null}

          {exchange.state === 'cancelled' && exchange.cancellationReason !== null ? (
            <Alert color="gray" variant="light">
              Cancelled by {exchange.cancellationInitiator ?? 'someone'}:{' '}
              {exchange.cancellationReason}
            </Alert>
          ) : null}

          {/* Offer & terms — collapsible once the conversation exists. While
              the exchange is still drafted or just submitted, terms ARE the
              point so default open. Once a thread is live the conversation is
              the primary surface; terms collapse to a one-line summary. */}
          <MotionSection delay={0.04}>
            <OfferAndTermsCollapsible
              exchange={exchange}
              defaultOpen={exchange.threadId === null || exchange.state === 'submitted'}
            />
          </MotionSection>

          {/* Post-state links — review, certificate, recap, open dispute.
              Distinct from the state-transition action bar so the visual
              hierarchy stays clear: state actions up top, navigation here. */}
          {hasPostStateLinks ? (
            <Group gap="sm" wrap="wrap">
              {exchange.state === 'completed' ? (
                <Link
                  to="/exchanges/$id/review"
                  params={{ id: exchange.id }}
                  style={{ textDecoration: 'underline' }}
                >
                  Write a review →
                </Link>
              ) : null}

              {exchange.state === 'completed' || exchange.state === 'closed' ? (
                <Link
                  to="/certificates/$exchangeId"
                  params={{ exchangeId: exchange.id }}
                  style={{ textDecoration: 'none' }}
                >
                  <Button component="span" tabIndex={-1} variant="light">
                    Download exchange certificate
                  </Button>
                </Link>
              ) : null}

              {(exchange.state === 'completed' || exchange.state === 'closed') &&
              recapMe !== undefined ? (
                <RecapCta exchangeId={exchange.id} recapMe={recapMe} />
              ) : null}

              {(exchange.state === 'confirmed' || exchange.state === 'completed') &&
              (viewer.role === 'artist' || viewer.role === 'host') ? (
                <Link
                  to="/disputes/open"
                  search={{ exchangeId: exchange.id }}
                  style={{
                    textDecoration: 'underline',
                    fontSize: 14,
                    color: 'var(--mantine-color-dimmed)',
                  }}
                >
                  Raise a concern
                </Link>
              ) : null}
            </Group>
          ) : null}

          {/* Share-this-exchange card — visible after the share endpoint
              resolves (state=completed AND both reviews released). Renders
              above the embedded thread so it doesn't get visually buried at
              the bottom of a long conversation. */}
          {exchange.state === 'completed' && share !== undefined ? (
            <MotionSection delay={0.06}>
              <ShareExchangeCard share={share} />
            </MotionSection>
          ) : null}

          {/* Smart Rider — generated touring document, flag-gated + confirmed +
              party only. `smartRiderEnabled` is server-computed with full
              flag+state context; the three worker routes remain the actual
              authority. */}
          {data.smartRiderEnabled &&
          exchange.state === 'confirmed' &&
          (viewer.role === 'artist' || viewer.role === 'host') ? (
            <MotionSection delay={0.07}>
              <SmartRiderPanel exchangeId={exchange.id} />
            </MotionSection>
          ) : null}

          {/* Conversation — the live surface. Embedded ThreadView reuses the
              same component as /inbox/$threadId so action/state parity is
              guaranteed across both URLs. Drafts (no thread yet) render an
              EmptyState with the submit CTA instead. */}
          <MotionSection delay={0.08}>
            <Stack gap="sm">
              <SectionTitle title="Conversation" order={4} />
              {exchange.threadId === null ? (
                <EmptyState
                  title="The conversation begins when you send the request."
                  body="The draft hasn't been sent yet. Once you submit, the host's reply lands here."
                />
              ) : (
                <ThreadView threadId={exchange.threadId} embedded />
              )}
            </Stack>
          </MotionSection>
        </Stack>
    </AppShell>
  );
}

/**
 * AddressCard — replaces the previous "Address visible inside the thread."
 * fallback with a clear two-state surface. When the address is visible to
 * the viewer we render it inline with a map link; otherwise we surface a
 * forward-looking explanation of when it unlocks.
 */
function AddressCard({
  state,
  venue,
  addressVisible,
}: {
  state: DetailState;
  venue: ExchangeDetailDto['venue'];
  addressVisible: boolean;
}) {
  const canShow = state === 'confirmed' && addressVisible && venue.addressLine !== null;

  return (
    <Card withBorder radius="md">
      <Stack gap="sm">
        <Title order={4}>{venue.name}</Title>
        <Text size="sm" c="dimmed">
          {venue.city}, {venue.country}
        </Text>
        {canShow ? (
          <Alert color="teal" variant="light">
            <Stack gap={4}>
              <Text size="sm" fw={500}>
                {venue.addressLine}
              </Text>
              <Anchor
                href={`https://www.openstreetmap.org/search?query=${encodeURIComponent(
                  `${venue.addressLine}, ${venue.city}`,
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                size="xs"
              >
                Open in maps ↗
              </Anchor>
            </Stack>
          </Alert>
        ) : state === 'confirmed' ? (
          <Text size="xs" c="dimmed">
            Address is visible to confirmed parties only.
          </Text>
        ) : (
          <Text size="xs" c="dimmed">
            Address shares automatically when the host confirms.
          </Text>
        )}
        <Link
          to="/venues/$slug"
          params={{ slug: venue.slug }}
          style={{
            textDecoration: 'underline',
            fontSize: 14,
            color: 'var(--mantine-color-brand-7)',
          }}
        >
          View venue
        </Link>
      </Stack>
    </Card>
  );
}

/**
 * OfferAndTermsCollapsible — the contract surface. Open by default while the
 * exchange is still being negotiated (drafted / submitted); collapses once a
 * thread exists, with a one-line summary peek so the offer's still scannable
 * without expanding.
 */
function OfferAndTermsCollapsible({
  exchange,
  defaultOpen,
}: {
  exchange: ExchangeDetailDto['exchange'];
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState<boolean>(defaultOpen);
  const summary = summariseOffer(exchange);

  return (
    <Card withBorder radius="md">
      <Stack gap="sm">
        <Group justify="space-between" wrap="nowrap" align="center">
          <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
            <Title order={5}>Offer & terms</Title>
            {!open && summary !== null ? (
              <Text size="sm" c="ink.6" lineClamp={1}>
                {summary}
              </Text>
            ) : null}
          </Stack>
          <Button
            variant="subtle"
            size="xs"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            {open ? 'Hide' : 'Show'}
          </Button>
        </Group>
        <Collapse expanded={open}>
          <Stack gap="md">
            <Stack gap={4}>
              <Text size="sm" fw={600}>
                Letter
              </Text>
              <Text style={{ whiteSpace: 'pre-wrap' }}>{exchange.openingLetter}</Text>
            </Stack>
            <OfferedSummary exchange={exchange} />
          </Stack>
        </Collapse>
      </Stack>
    </Card>
  );
}

/**
 * One-line "bed offered · dinner · $200 stipend" peek for the collapsed
 * offer-and-terms card. Returns null when nothing notable is set so the
 * collapsed header reads cleanly instead of "–".
 */
function summariseOffer(exchange: ExchangeDetailDto['exchange']): string | null {
  const bits: string[] = [];
  if (exchange.accommodationKind !== null && exchange.accommodationKind !== undefined) {
    const name = SLEEPING_AMENITIES.find((a) => a.slug === exchange.accommodationKind)?.name;
    if (name !== undefined) bits.push(name.toLowerCase());
  }
  const meals: string[] = [];
  if (exchange.mealsBreakfast) meals.push('breakfast');
  if (exchange.mealsLunch) meals.push('lunch');
  if (exchange.mealsDinner) meals.push('dinner');
  if (meals.length > 0) bits.push(meals.join(' + '));
  if (exchange.stipendOffered && exchange.stipendAmountMinor !== null && exchange.stipendCurrency) {
    const major = (exchange.stipendAmountMinor / 100).toFixed(0);
    bits.push(`$${major} ${exchange.stipendCurrency} stipend`);
  }
  if (exchange.travelContributionOffered) bits.push('travel');
  if (exchange.equipmentOffered) bits.push('gear');
  if (exchange.promotionOffered) bits.push('promo');
  if (exchange.recordingOffered) bits.push('recording');
  return bits.length === 0 ? null : bits.join(' · ');
}

/**
 * ShareExchangeCard — unchanged from before. Renders only when the share
 * endpoint resolved.
 */
function ShareExchangeCard({ share }: { share: ShareDto }) {
  const canNativeShare =
    typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const canCopy =
    typeof navigator !== 'undefined' &&
    typeof navigator.clipboard?.writeText === 'function';

  useEffect(() => {
    if (!canNativeShare && !canCopy) return;
    track('share.exchange_card_opened', { exchangeId: share.exchangeId });
  }, [share.exchangeId, canNativeShare, canCopy]);

  if (!canNativeShare && !canCopy) return null;

  async function onNativeShare(): Promise<void> {
    track('share.exchange_card_action', { method: 'native', exchangeId: share.exchangeId });
    try {
      await navigator.share({
        title: 'WWAAVE',
        text: share.shareText,
        url: share.canonicalUrl,
      });
    } catch {
      // User-cancelled — no toast.
    }
  }

  function onPostToX(): void {
    track('share.exchange_card_action', { method: 'twitter', exchangeId: share.exchangeId });
    window.open(share.twitterIntent, '_blank', 'noopener,noreferrer');
  }

  async function onCopyLink(): Promise<void> {
    track('share.exchange_card_action', { method: 'copy', exchangeId: share.exchangeId });
    try {
      await navigator.clipboard.writeText(share.canonicalUrl);
      showToast({ kind: 'success', message: 'Link copied to your clipboard.' });
    } catch {
      showToast({
        kind: 'error',
        message: "Couldn't copy automatically. Try selecting the link manually.",
      });
    }
  }

  return (
    <Card
      withBorder
      radius="md"
      style={{ background: 'light-dark(var(--mantine-color-brand-0), var(--mantine-color-dark-6))' }}
    >
      <Stack gap="md">
        <Title order={4}>Share this exchange</Title>
        <img
          src={share.imageUrl}
          alt={`${share.artist.displayName} played ${share.venue.name} in ${share.venue.city}`}
          loading="lazy"
          style={{
            display: 'block',
            width: '100%',
            maxWidth: 300,
            height: 'auto',
            borderRadius: 8,
          }}
        />
        <Text
          size="sm"
          fs="italic"
          c="dimmed"
          style={{ borderLeft: '3px solid var(--mantine-color-brand-4)', paddingLeft: 12 }}
        >
          {share.shareText}
        </Text>
        <Group gap="sm">
          {canNativeShare ? (
            <Button onClick={onNativeShare} variant="filled">
              Share
            </Button>
          ) : null}
          <Button onClick={onPostToX} variant="default">
            Post to X
          </Button>
          {canCopy ? (
            <Button onClick={onCopyLink} variant="default">
              Copy link
            </Button>
          ) : null}
        </Group>
        <Text size="xs" c="dimmed">
          When you post, the link previews with a card showing your exchange.
        </Text>
      </Stack>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Safety nudges — shown while the exchange is `confirmed` (a real stay is
// locked in + the address has been revealed). Copy + a client-side share
// affordance only; nothing here touches the backend.
// ---------------------------------------------------------------------------

/**
 * ShareYourStayCard — §6.4. The travelling artist gets a calm prompt to let a
 * trusted contact know the venue + dates before they go. Web Share API where
 * available, copy-to-clipboard everywhere else. The message is assembled from
 * data already on the page, so this stays purely client-side.
 */
function ShareYourStayCard({
  venue,
  startDate,
  endDate,
  addressLine,
}: {
  venue: ExchangeDetailDto['venue'];
  startDate: string;
  endDate: string;
  addressLine: string | null;
}) {
  const canNativeShare =
    typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const canCopy =
    typeof navigator !== 'undefined' &&
    typeof navigator.clipboard?.writeText === 'function';

  const shareText = buildStayShareText({ venue, startDate, endDate, addressLine });

  async function onNativeShare(): Promise<void> {
    try {
      await navigator.share({ title: 'My WWAAVE stay', text: shareText });
    } catch {
      // User-cancelled — no toast.
    }
  }

  async function onCopy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(shareText);
      showToast({ kind: 'success', message: 'Copied. Send it to someone you trust.' });
    } catch {
      showToast({
        kind: 'error',
        message: "Couldn't copy automatically. You can select the message above and copy it.",
      });
    }
  }

  return (
    <Card withBorder radius="md">
      <Stack gap="sm">
        <Title order={5}>Let someone know where you'll be</Title>
        <Text size="sm" c="ink.6">
          Before you travel, it's worth sharing the venue and your dates with a
          friend or family member. A quick message is plenty.
        </Text>
        <Text
          size="sm"
          style={{
            whiteSpace: 'pre-wrap',
            borderLeft: '3px solid var(--mantine-color-tide-5)',
            paddingLeft: 12,
          }}
        >
          {shareText}
        </Text>
        <Group gap="sm">
          {canNativeShare ? (
            <Button onClick={onNativeShare} variant="filled">
              Share
            </Button>
          ) : null}
          {canCopy ? (
            <Button onClick={onCopy} variant={canNativeShare ? 'default' : 'filled'}>
              Copy details
            </Button>
          ) : null}
        </Group>
      </Stack>
    </Card>
  );
}

/**
 * Builds the plain-text message the artist shares with a trusted contact.
 * Venue + dates always; the address line too once it's been revealed to the
 * artist, since that's the detail a contact most wants if they ever need it.
 */
function buildStayShareText({
  venue,
  startDate,
  endDate,
  addressLine,
}: {
  venue: ExchangeDetailDto['venue'];
  startDate: string;
  endDate: string;
  addressLine: string | null;
}): string {
  const lines = [
    "Sharing my travel plans through WWAAVE so you know where I'll be.",
    '',
    `Venue: ${venue.name}, ${venue.city}`,
    `Dates: ${formatStayDates(startDate, endDate)}`,
  ];
  if (addressLine !== null && addressLine.length > 0) {
    lines.push(`Address: ${addressLine}`);
  }
  return lines.join('\n');
}

/** "1 Jul 2026 to 3 Jul 2026" — falls back to the raw ISO strings if unparseable. */
function formatStayDates(startIso: string, endIso: string): string {
  const start = parseIsoDate(startIso);
  const end = parseIsoDate(endIso);
  if (start === null || end === null) return `${startIso} to ${endIso}`;
  const fmt = (d: Date): string =>
    d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  return `${fmt(start)} to ${fmt(end)}`;
}

/**
 * IdentityMatchNudge — §3. One-line reassurance for the host: check the person
 * arriving matches the artist's existing profile photo. The photo's already
 * public, so we show it here as the reference. Copy + an existing image, no
 * stored selfie and no new tech.
 */
function IdentityMatchNudge({ artist }: { artist: ExchangeDetailDto['artist'] }) {
  return (
    <Card withBorder radius="md">
      <Group gap="md" wrap="nowrap" align="center">
        <Avatar
          src={artist.profilePhotoUrl}
          alt={artist.displayName}
          radius="xl"
          size="md"
        />
        <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
          <Text size="sm">
            When {artist.displayName} arrives, a quick glance to check they match
            their profile photo keeps everyone comfortable.
          </Text>
          {artist.slug !== null ? (
            <Link
              to="/artists/$slug"
              params={{ slug: artist.slug }}
              style={{ textDecoration: 'none', width: 'fit-content' }}
            >
              <Anchor component="span" size="xs">
                View their profile
              </Anchor>
            </Link>
          ) : null}
        </Stack>
      </Group>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Recap CTA
// ---------------------------------------------------------------------------

function RecapCta({
  exchangeId,
  recapMe,
}: {
  exchangeId: string;
  recapMe: RecapMeDto;
}) {
  if (recapMe.recap === null) {
    if (!recapMe.eligible) return null;
    return (
      <Link
        to="/exchanges/$id/recap"
        params={{ id: exchangeId }}
        style={{ textDecoration: 'none' }}
      >
        <Button component="span" tabIndex={-1} variant="filled">
          Draft a recap
        </Button>
      </Link>
    );
  }

  const recap = recapMe.recap;
  if (recap.state === 'published' && recap.slug !== null) {
    return (
      <Group gap="xs" wrap="nowrap">
        <Link
          to="/recaps/$slug"
          params={{ slug: recap.slug }}
          style={{ textDecoration: 'none' }}
        >
          <Button component="span" tabIndex={-1} variant="filled" color="teal">
            View live recap
          </Button>
        </Link>
        <StateBadge entity="exchange_recap" state={recap.state} size="sm" />
      </Group>
    );
  }

  return (
    <Group gap="xs" wrap="nowrap">
      <Link
        to="/exchanges/$id/recap"
        params={{ id: exchangeId }}
        style={{ textDecoration: 'none' }}
      >
        <Button component="span" tabIndex={-1} variant="default">
          Continue your recap
        </Button>
      </Link>
      <StateBadge entity="exchange_recap" state={recap.state} size="sm" />
    </Group>
  );
}

// ---------------------------------------------------------------------------
// Day-of-arrival surface — unchanged
// ---------------------------------------------------------------------------

type DayOfArrivalPhase =
  | { kind: 'today' }
  | { kind: 'tomorrow' }
  | { kind: 'within_week'; daysUntil: number }
  | { kind: 'staying' };

function computeDayOfArrival(
  state: DetailState,
  startDateIso: string,
  endDateIso: string,
): DayOfArrivalPhase | null {
  if (state !== 'confirmed') return null;
  const start = parseIsoDate(startDateIso);
  const end = parseIsoDate(endDateIso);
  if (start === null || end === null) return null;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msPerDay = 24 * 60 * 60 * 1000;

  if (today.getTime() >= start.getTime() && today.getTime() <= end.getTime()) {
    return { kind: 'staying' };
  }

  const daysUntilStart = Math.round((start.getTime() - today.getTime()) / msPerDay);
  if (daysUntilStart < 0) return null;
  if (daysUntilStart > 7) return null;
  if (daysUntilStart === 0) return { kind: 'today' };
  if (daysUntilStart === 1) return { kind: 'tomorrow' };
  return { kind: 'within_week', daysUntil: daysUntilStart };
}

function parseIsoDate(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m === null) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  return new Date(y, mo - 1, d);
}

function DayOfArrivalCard(props: {
  phase: DayOfArrivalPhase;
  venueName: string;
  address: string | null;
}) {
  const { phase, venueName, address } = props;
  const heading =
    phase.kind === 'staying'
      ? `Staying tonight at ${venueName}.`
      : phase.kind === 'today'
        ? 'Stay starts today.'
        : phase.kind === 'tomorrow'
          ? 'Stay starts in 1 day.'
          : `Stay starts in ${phase.daysUntil} days.`;

  return (
    <Card
      withBorder
      radius="md"
      padding="lg"
      style={{ background: 'light-dark(var(--mantine-color-brand-0), var(--mantine-color-dark-6))' }}
    >
      <Stack gap="sm">
        <Title order={3} c="brand.7">
          {heading}
        </Title>
        {address !== null ? (
          <Text size="sm">{address}</Text>
        ) : (
          <Text size="sm" c="dimmed">
            The host will share arrival details in the thread.
          </Text>
        )}
        <Group gap="md" mt="xs">
          <Text size="xs" c="dimmed">
            · Confirm load-in
          </Text>
          <Text size="xs" c="dimmed">
            · Bring the rider
          </Text>
          <Text size="xs" c="dimmed">
            · Check parking
          </Text>
        </Group>
      </Stack>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// What was offered — renders the structured non-default offer fields.
// ---------------------------------------------------------------------------

type OfferedExchange = ExchangeDetailDto['exchange'];

function OfferedSummary({ exchange }: { exchange: OfferedExchange }) {
  const accommodationKind = exchange.accommodationKind ?? null;
  const accommodationNotes = exchange.accommodationNotes ?? null;
  const mealsBreakfast = exchange.mealsBreakfast ?? false;
  const mealsLunch = exchange.mealsLunch ?? false;
  const mealsDinner = exchange.mealsDinner ?? false;
  const mealsNotes = exchange.mealsNotes ?? null;
  const travelOffered = exchange.travelContributionOffered ?? false;
  const travelAmount = exchange.travelContributionAmountMinor ?? null;
  const travelCurrency = exchange.travelContributionCurrency ?? null;
  const equipmentOffered = exchange.equipmentOffered ?? false;
  const equipmentNotes = exchange.equipmentNotes ?? null;
  const promotionOffered = exchange.promotionOffered ?? false;
  const promotionNotes = exchange.promotionNotes ?? null;
  const recordingOffered = exchange.recordingOffered ?? false;
  const recordingNotes = exchange.recordingNotes ?? null;

  const meals: string[] = [];
  if (mealsBreakfast) meals.push('breakfast');
  if (mealsLunch) meals.push('lunch');
  if (mealsDinner) meals.push('dinner');

  const accommodationName =
    accommodationKind !== null
      ? SLEEPING_AMENITIES.find((a) => a.slug === accommodationKind)?.name ?? accommodationKind
      : null;

  const extras: { label: string; notes: string | null }[] = [];
  if (travelOffered) {
    let label = 'Travel contribution';
    if (travelAmount !== null && travelCurrency !== null) {
      const major = (travelAmount / 100).toFixed(0);
      label = `Travel contribution: $${major} ${travelCurrency}`;
    }
    extras.push({ label, notes: null });
  }
  if (equipmentOffered) extras.push({ label: 'Equipment', notes: equipmentNotes });
  if (promotionOffered) extras.push({ label: 'Promotion', notes: promotionNotes });
  if (recordingOffered) extras.push({ label: 'Recording', notes: recordingNotes });

  const hasAccommodation = accommodationName !== null;
  const hasMeals = meals.length > 0 || (mealsNotes !== null && mealsNotes.length > 0);
  const hasExtras = extras.length > 0;

  if (!hasAccommodation && !hasMeals && !hasExtras) return null;

  return (
    <Stack gap="sm">
      {hasAccommodation ? (
        <Stack gap={2}>
          <Text size="sm">
            <Text component="span" fw={600}>
              Accommodation:
            </Text>{' '}
            {accommodationName}
          </Text>
          {accommodationNotes !== null && accommodationNotes.length > 0 ? (
            <Text size="sm" c="dimmed">
              {accommodationNotes}
            </Text>
          ) : null}
        </Stack>
      ) : null}

      {hasMeals ? (
        <Stack gap={2}>
          <Text size="sm">
            <Text component="span" fw={600}>
              Meals:
            </Text>{' '}
            {meals.length > 0 ? meals.join(', ') : '–'}
          </Text>
          {mealsNotes !== null && mealsNotes.length > 0 ? (
            <Text size="sm" c="dimmed">
              {mealsNotes}
            </Text>
          ) : null}
        </Stack>
      ) : null}

      {hasExtras ? (
        <Stack gap={2}>
          <Text size="sm" fw={600}>
            Plus:
          </Text>
          <Stack gap={4}>
            {extras.map((x) => (
              <Stack gap={0} key={x.label}>
                <Text size="sm">{x.label}</Text>
                {x.notes !== null && x.notes.length > 0 ? (
                  <Text size="sm" c="dimmed">
                    {x.notes}
                  </Text>
                ) : null}
              </Stack>
            ))}
          </Stack>
        </Stack>
      ) : null}
    </Stack>
  );
}

